import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormsModule, FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';

import { NzButtonModule } from 'ng-zorro-antd/button';
import { NzFormModule } from 'ng-zorro-antd/form';
import { NzInputModule } from 'ng-zorro-antd/input';
import { NzSelectModule, NzSelectOptionInterface } from 'ng-zorro-antd/select';
import { NzStepsModule } from 'ng-zorro-antd/steps';
import { NzAlertModule } from 'ng-zorro-antd/alert';
import { NzCheckboxModule } from 'ng-zorro-antd/checkbox';
import { NzRadioModule } from 'ng-zorro-antd/radio';
import { NzDividerModule } from 'ng-zorro-antd/divider';
import { NzMessageService } from 'ng-zorro-antd/message';
import { TranslateModule, TranslateService } from '@ngx-translate/core';

import {
  IntegrationsService,
  IntegrationConnection,
  IntegrationCredentialField,
  IntegrationProviderCatalogItem,
} from './integrations.service';
import { AuthService } from '../services/auth.service';
import { shouldSuppressGenericError } from '../utils/http-error.utils';

type WizardStep = 0 | 1 | 2 | 3 | 4;

@Component({
  selector: 'app-integration-setup-wizard',
  standalone: true,
  imports: [
    CommonModule,
    RouterModule,
    FormsModule,
    ReactiveFormsModule,
    NzButtonModule,
    NzFormModule,
    NzInputModule,
    NzSelectModule,
    NzStepsModule,
    NzAlertModule,
    NzCheckboxModule,
    NzRadioModule,
    NzDividerModule,
    TranslateModule
  ],
  templateUrl: './integration-setup-wizard.component.html',
  styleUrls: ['./integration-setup-wizard.component.less']
})
export class IntegrationSetupWizardComponent implements OnInit {
  step: WizardStep = 0;

  provider: IntegrationProviderCatalogItem | null = null;
  existingConnection: IntegrationConnection | null = null;
  connectionId: string | null = null;

  // Step 1 credentials form (manual/api_key)
  credentialsForm!: FormGroup;

  // Step 2 modules
  modulesForm!: FormGroup<{ enabled: FormControl<string[]> }>;

  // Step 3 scope
  scopeForm!: FormGroup<{ userIds: FormControl<string[]> }>;
  scopes: NzSelectOptionInterface[] = [];
  capabilityOptions: Array<{ label: string; description: string; value: string }> = [];

  // Step 4 scheduling
  scheduleForm!: FormGroup<{
    syncMode: FormControl<string>;
    hour: FormControl<number>;
    minute: FormControl<number>;
    dayOfWeek: FormControl<number>;
    paused: FormControl<boolean>;
  }>;

  // loading flags
  loadingProvider = false;
  loadingConnections = false;
  saving = false;
  testing = false;
  loadingScopes = false;
  oauthLoading = false;
  integrationsAllowed = 0;
  integrationsUsed = 0;

  constructor(
    private fb: FormBuilder,
    private integrationsService: IntegrationsService,
    private message: NzMessageService,
    private router: Router,
    private route: ActivatedRoute,
    private authService: AuthService,
    private translate: TranslateService
  ) {
    // init forms here (fb is available only after DI runs)
    this.credentialsForm = this.fb.group({});

    this.modulesForm = this.fb.nonNullable.group({
      enabled: this.fb.nonNullable.control<string[]>([])
    });

    this.scopeForm = this.fb.nonNullable.group({
      userIds: this.fb.nonNullable.control<string[]>([])
    });

    this.scheduleForm = this.fb.nonNullable.group({
      syncMode: this.fb.nonNullable.control<string>('manual'),
      hour: this.fb.nonNullable.control<number>(1),
      minute: this.fb.nonNullable.control<number>(0),
      dayOfWeek: this.fb.nonNullable.control<number>(1),
      paused: this.fb.nonNullable.control<boolean>(false),
    });
  }

  ngOnInit(): void {
    const providerKey = this.route.snapshot.paramMap.get('provider');
    if (!providerKey) {
      this.message.error(this.translate.instant('INTEGRATION.SETUP_WIZARD.ERROR_PROVIDER_MISSING'));
      return;
    }

    this.loadingProvider = true;

    this.integrationsService.listProviders().subscribe({
      next: (providers) => {
        this.provider = (providers || []).find(p => p.provider === providerKey) || null;
        this.loadingProvider = false;

        if (!this.provider) {
          this.message.error(this.translate.instant('INTEGRATION.SETUP_WIZARD.NOT_FOUND_TITLE'));
          return;
        }

        // build credential form if needed
        this.buildCredentialsForm(this.provider.credentialSchema || []);

        this.capabilityOptions = (this.provider.capabilities || []).map(c => ({
          label: this.resolveCapabilityLabel(c),
          description: this.resolveCapabilityDescription(c),
          value: c.key
        }));

        // load existing connection (if any)
        this.loadExistingConnection(providerKey);

        // OAuth callback handling: setup route receives ?code & ?state
        const code = this.route.snapshot.queryParamMap.get('code');
        const state = this.route.snapshot.queryParamMap.get('state');
        if (code && state && this.provider.authType === 'oauth2') {
          this.finishOAuth(code, state);
        }
      },
      error: () => {
        this.loadingProvider = false;
        this.message.error(this.translate.instant('INTEGRATION.SETUP_WIZARD.ERROR_LOAD_PROVIDERS'));
      }
    });
  }

  get hasCredentialsWritePermission(): boolean {
    return this.authService.hasPermission('company.integrations.credentials.write');
  }

  private canCreateConnectionForProvider(): boolean {
    const hasExisting = !!this.existingConnection || !!this.connectionId;
    return hasExisting || this.integrationsUsed < this.integrationsAllowed;
  }

  isProviderAvailable(): boolean {
    return this.provider?.status === 'available';
  }

  goBack(): void {
    this.router.navigate(['/company/integrations']);
  }

  buildCredentialsForm(schema: IntegrationCredentialField[]): void {
    const group: Record<string, any> = {};
    schema.forEach((field) => {
      const validators = field.required ? [Validators.required] : [];
      group[field.key] = [field.default ?? '', validators];
    });
    this.credentialsForm = this.fb.group(group);
  }

  getInputType(field: IntegrationCredentialField): string {
    if (field.type === 'password') return 'password';
    if (field.type === 'url') return 'url';
    return 'text';
  }

  loadExistingConnection(providerKey: string): void {
    this.loadingConnections = true;
    this.integrationsService.listConnections().subscribe({
      next: (res) => {
        const items = res.items || [];
        this.integrationsAllowed = Number(res?.limits?.allowed || 0);
        this.integrationsUsed = Number(res?.limits?.used || 0);
        const match = items.find(c => (c.provider || '').toLowerCase() === providerKey.toLowerCase()) || null;

        this.existingConnection = match;
        this.connectionId = match?.id || null;

        // prefill settings into forms
        const enabled = match?.settings?.enabledCapabilities || this.provider?.defaultEnabledCapabilities || [];
        this.modulesForm.patchValue({ enabled });

        const userIds = match?.settings?.scope?.userIds || [];
        this.scopeForm.patchValue({ userIds });

        this.scheduleForm.patchValue({
          syncMode: match?.settings?.syncMode || 'manual',
          paused: !!match?.settings?.paused,
          hour: match?.settings?.schedule?.hour ?? 1,
          minute: match?.settings?.schedule?.minute ?? 0,
          dayOfWeek: match?.settings?.schedule?.dayOfWeek ?? 1,
        });

        this.loadingConnections = false;
      },
      error: () => {
        this.loadingConnections = false;
      }
    });
  }

  // Step navigation
  canGoNext(): boolean {
    if (!this.provider) return false;
    if (!this.isProviderAvailable()) return false;

    // Step 0: must have connectionId for oauth OR credentials valid for manual/api_key
    if (this.step === 0) {
      if (this.provider.authType === 'oauth2') return !!this.connectionId;
      return this.credentialsForm.valid && !!this.connectionId;
    }

    return true;
  }

  next(): void {
    if (this.step < 4) this.step = (this.step + 1) as WizardStep;
  }

  prev(): void {
    if (this.step > 0) this.step = (this.step - 1) as WizardStep;
  }

  // Step 0 actions: manual/api_key
  saveCredentials(): void {
    if (!this.provider) return;
    if (!this.isProviderAvailable()) return;
    if (!this.hasCredentialsWritePermission) {
      this.message.warning(this.translate.instant('ERRORS.INTEGRATIONS_ADMIN_ONLY'));
      return;
    }

    if (this.provider.authType === 'oauth2') {
      this.message.info(this.translate.instant('INTEGRATION.SETUP_WIZARD.OAUTH_ONLY_CONNECT'));
      return;
    }

    if (!this.canCreateConnectionForProvider()) {
      this.message.warning(this.translate.instant('ERRORS.INTEGRATIONS_LIMIT_REACHED_CONTACT'));
      return;
    }

    if (this.credentialsForm.invalid) {
      this.message.warning(this.translate.instant('INTEGRATION.SETUP_WIZARD.FILL_REQUIRED'));
      return;
    }

    this.saving = true;
    const credentials = { ...this.credentialsForm.value };

    // upsert connection
    this.integrationsService.createOrUpdateConnection(this.provider.provider, credentials).subscribe({
      next: (res) => {
        this.connectionId = res.id;
        this.message.success(this.translate.instant(this.existingConnection ? 'INTEGRATION.SETUP_WIZARD.CREDENTIALS_UPDATED' : 'INTEGRATION.SETUP_WIZARD.CONNECTION_SAVED'));
        this.saving = false;
        this.loadExistingConnection(this.provider!.provider);
      },
      error: (err) => {
        this.saving = false;
        if (shouldSuppressGenericError(err)) {
          return;
        }
        this.message.error(err?.error?.message || this.translate.instant('INTEGRATION.SETUP_WIZARD.ERROR_SAVE_CONNECTION'));
      }
    });
  }

  testConnection(): void {
    if (!this.connectionId) {
      this.message.warning(this.translate.instant('INTEGRATION.SETUP_WIZARD.CONNECT_FIRST'));
      return;
    }
    this.testing = true;
    this.integrationsService.testConnection(this.connectionId).subscribe({
      next: (res) => {
        this.testing = false;
        this.message.success(res.ok ? this.translate.instant('INTEGRATION.OVERVIEW.CONNECTION_OK') : (res.message || this.translate.instant('INTEGRATION.OVERVIEW.CONNECTION_FAILED')));
      },
      error: (err) => {
        this.testing = false;
        if (shouldSuppressGenericError(err)) {
          return;
        }
        this.message.error(err?.error?.message || this.translate.instant('INTEGRATION.OVERVIEW.ERROR_TEST'));
      }
    });
  }

  // OAuth
  startOAuth(): void {
    if (!this.provider) return;
    if (!this.isProviderAvailable()) return;
    if (!this.hasCredentialsWritePermission) {
      this.message.warning(this.translate.instant('ERRORS.INTEGRATIONS_ADMIN_ONLY'));
      return;
    }
    if (!this.canCreateConnectionForProvider()) {
      this.message.warning(this.translate.instant('ERRORS.INTEGRATIONS_LIMIT_REACHED_CONTACT'));
      return;
    }

    const redirectUri = window.location.origin + `/company/integrations/${this.provider.provider}/setup`;
    this.oauthLoading = true;

    this.integrationsService.startOAuth(this.provider.provider, redirectUri).subscribe({
      next: (res) => {
        this.oauthLoading = false;
        window.location.href = res.url;
      },
      error: (err) => {
        this.oauthLoading = false;
        if (shouldSuppressGenericError(err)) {
          return;
        }
        this.message.error(err?.error?.message || this.translate.instant('INTEGRATION.SETUP_WIZARD.ERROR_OAUTH_START'));
      }
    });
  }

  finishOAuth(code: string, state: string): void {
    if (!this.provider) return;

    const redirectUri = window.location.origin + `/company/integrations/${this.provider.provider}/setup`;
    this.oauthLoading = true;

    this.integrationsService.finishOAuth(this.provider.provider, code, state, redirectUri).subscribe({
      next: (res) => {
        this.oauthLoading = false;
        this.connectionId = res.connectionId;
        this.message.success(this.translate.instant('INTEGRATION.SETUP_WIZARD.OAUTH_CONNECTED'));
        // remove query params (clean URL)
        this.router.navigate([], {
          relativeTo: this.route,
          queryParams: {},
          replaceUrl: true
        });
        this.loadExistingConnection(this.provider!.provider);
      },
      error: (err) => {
        this.oauthLoading = false;
        if (shouldSuppressGenericError(err)) {
          return;
        }
        this.message.error(err?.error?.message || this.translate.instant('INTEGRATION.SETUP_WIZARD.ERROR_OAUTH_FINISH'));
      }
    });
  }

  // Step 3 scopes
  loadScopes(): void {
    if (!this.connectionId) {
      this.message.warning(this.translate.instant('INTEGRATION.SETUP_WIZARD.CONNECT_FIRST'));
      return;
    }
    this.loadingScopes = true;
    this.integrationsService.listScopes(this.connectionId).subscribe({
      next: (res) => {
        const userIds = res.userIds || [];
        this.scopes = userIds.map(u => ({ label: u.label ?? u.id, value: u.id }));
        this.loadingScopes = false;
      },
      error: () => {
        this.loadingScopes = false;
      }
    });
  }

  // save settings (modules/scope/schedule)
  saveAllSettings(): void {
    if (!this.connectionId) {
      this.message.warning(this.translate.instant('INTEGRATION.SETUP_WIZARD.CONNECT_FIRST'));
      return;
    }

    const payload = {
      settings: {
        enabledCapabilities: this.modulesForm.value.enabled || [],
        scope: { userIds: this.scopeForm.value.userIds || [] },
        syncMode: this.scheduleForm.value.syncMode,
        paused: this.scheduleForm.value.paused,
        schedule: {
          hour: this.scheduleForm.value.hour,
          minute: this.scheduleForm.value.minute,
          dayOfWeek: this.scheduleForm.value.dayOfWeek,
        }
      }
    };

    this.saving = true;
    this.integrationsService.updateConnection(this.connectionId, payload).subscribe({
      next: () => {
        this.saving = false;
        this.message.success(this.translate.instant('INTEGRATION.SETUP_WIZARD.SETTINGS_SAVED'));
        this.router.navigate(['/company/integrations', this.connectionId, 'status']);
      },
      error: (err) => {
        this.saving = false;
        if (shouldSuppressGenericError(err)) {
          return;
        }
        this.message.error(err?.error?.message || this.translate.instant('INTEGRATION.SETUP_WIZARD.ERROR_SAVE_SETTINGS'));
      }
    });
  }

  // helpers for UI
  capabilities(): { key: string; label: string; description?: string }[] {
    return this.provider?.capabilities || [];
  }

  toggleModule(key: string, checked: boolean): void {
    const current = this.modulesForm.value.enabled || [];
    const next = checked
      ? Array.from(new Set([...current, key]))
      : current.filter(x => x !== key);

    this.modulesForm.patchValue({ enabled: next });
  }

  syncModeHelp(mode: string): string {
    if (mode === 'manual') return this.translate.instant('INTEGRATION.SETUP_WIZARD.SYNC_MODE_HELP.MANUAL');
    if (mode === 'hourly') return this.translate.instant('INTEGRATION.SETUP_WIZARD.SYNC_MODE_HELP.HOURLY');
    if (mode === 'daily') return this.translate.instant('INTEGRATION.SETUP_WIZARD.SYNC_MODE_HELP.DAILY');
    if (mode === 'weekly') return this.translate.instant('INTEGRATION.SETUP_WIZARD.SYNC_MODE_HELP.WEEKLY');
    return '';
  }

  private resolveCapabilityLabel(capability: { label?: string; labelKey?: string }): string {
    return capability.labelKey ? this.translate.instant(capability.labelKey) : (capability.label || '');
  }

  private resolveCapabilityDescription(capability: { description?: string; descriptionKey?: string }): string {
    return capability.descriptionKey
      ? this.translate.instant(capability.descriptionKey)
      : (capability.description || '');
  }
}
